import React from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { CardQuestion } from '../../types';

interface TriggerStepProps {
  questions: CardQuestion[];
  selectedQuestionId: string;
  selectedValues: string[];
  onValuesChange: (values: string[]) => void;
  onPathNameChange: (name: string) => void;
  pathName: string;
  usedOptionValues?: string[];
}

export function TriggerStep({
  questions,
  selectedQuestionId,
  selectedValues,
  onValuesChange,
  onPathNameChange,
  pathName,
  usedOptionValues = [],
}: TriggerStepProps) {
  const triggerQuestion = questions.find((q) => q.id === selectedQuestionId);
  const availableOptions = triggerQuestion?.config.options || [];
  const usedSet = new Set(usedOptionValues);

  const handleOptionToggle = (option: string) => {
    if (usedSet.has(option) && !selectedValues.includes(option)) return;
    if (selectedValues.includes(option)) {
      onValuesChange(selectedValues.filter((v) => v !== option));
    } else {
      onValuesChange([...selectedValues, option]);
    }
  };

  const isOptionDisabled = (option: string) =>
    usedSet.has(option) && !selectedValues.includes(option);

  return (
    <div>
      {triggerQuestion && availableOptions.length > 0 && (
        <div className="mb-3">
          <p className="mb-0.5 text-sm">
            With one of these answers in Q{triggerQuestion.number}:
          </p>
          <p className="mb-2 block text-xs text-muted-foreground">
            Select one or more; respondents who pick any of these will follow this shared path.
            {usedOptionValues.length > 0 && (
              <> Options already used by another path are disabled.</>
            )}
          </p>
          <div className="flex flex-col gap-2">
            {availableOptions.map((option) => {
              const disabled = isOptionDisabled(option);
              return (
                <div
                  key={option}
                  className={cn(
                    'flex items-center gap-2',
                    disabled && 'opacity-70'
                  )}
                >
                  <Checkbox
                    id={`option-${option}`}
                    checked={selectedValues.includes(option)}
                    disabled={disabled}
                    onCheckedChange={() => handleOptionToggle(option)}
                  />
                  <Label
                    htmlFor={`option-${option}`}
                    className={cn(disabled && 'cursor-not-allowed')}
                  >
                    {option}
                  </Label>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <Label htmlFor="path-name">Path Name (optional)</Label>
        <Input
          id="path-name"
          value={pathName}
          onChange={(e) => onPathNameChange(e.target.value)}
          placeholder="e.g., Designer Path"
          className="mt-1"
        />
        <p className="mt-1 text-xs text-muted-foreground">
          Give this path a friendly name to identify it
        </p>
      </div>
    </div>
  );
}
